import { Fragment } from "react";
import { createPath } from "@/service/routing/routing";
import { cn } from "@/lib/utils";
import {
  getPriceForRefund,
  getPriceForRefundOther,
  getPresentOtherRefunds,
  mergeVersionsByBox,
} from "../service";
import { MedBoxList } from "./med-box-list";
import { FileText } from "lucide-react";

interface MedSubgroupsListMobileProps {
  subgroups: MedGroupPreviewVM["versionSubgroups"];
  groupLink: string;
  superGroup: MedGroupPreviewVM["superGroup"];
  headless: boolean;
}

export function MedSubgroupsListMobile({
  subgroups,
  groupLink,
  superGroup,
  headless,
}: MedSubgroupsListMobileProps) {
  return (
    <>
      {subgroups.map(({ versions, medVersionKind }, index) => {
        const versionGroupLink = createPath("meds.group.show", {
          slug: versions[0].groupSlug,
          id: versions[0].groupId,
        });
        const versionIsFromActiveGroup =
          superGroup && headless && groupLink === versionGroupLink;
        return (
          <div
            className={cn(
              "border-b first:mt-0 last:mb-0 last:border-b-0",
              versionIsFromActiveGroup && "bg-[#FAFAFA]",
            )}
            key={`${medVersionKind}-${index}`}
          >
            {mergeVersionsByBox(versions).map(
              ({ variant, boxes }, subindex) => {
                const priceForRefundFull = getPriceForRefund(
                  variant.prices,
                  "100%",
                  versionGroupLink,
                );
                const priceForRefundFifty = getPriceForRefund(
                  variant.prices,
                  "50%",
                  versionGroupLink,
                );
                const priceForRefundThirty = getPriceForRefund(
                  variant.prices,
                  "30%",
                  versionGroupLink,
                );
                const priceForRefundR = getPriceForRefund(
                  variant.prices,
                  "R",
                  versionGroupLink,
                );

                const versionTitleStyle = "font-semibold text-black text-sm";

                const labelStyle =
                  "text-zinc-500 text-sm font-normal w-[100px] shrink-0";
                const valueStyle = cn(
                  "text-black text-sm font-normal",
                  variant.disabled && "opacity-25",
                );

                const chplLink = variant.chplLink && (
                  <div
                    className="hover:text-remedium-blue mr-2 inline-flex translate-y-0.5 transition-all"
                    data-title={"ChPL"}
                  >
                    <a
                      href={createPath("meds.chpl", { link: variant.chplLink })}
                      target={"_blank"}
                    >
                      <FileText size={16} />
                    </a>
                  </div>
                );

                return (
                  <div
                    className="border-b border-[#E6E8ED] px-4 py-3 last:border-b-0"
                    key={`${subindex}-${variant.name}`}
                  >
                    {/* Name/Title */}
                    <div
                      className={cn(
                        "mb-2 inline-flex",
                        variant.disabled && "opacity-25",
                      )}
                    >
                      {chplLink}
                      {versionIsFromActiveGroup || (!superGroup && headless) ? (
                        <div
                          className={cn(
                            versionTitleStyle,
                            versionIsFromActiveGroup && "text-teal-600",
                          )}
                        >
                          {variant.name}
                        </div>
                      ) : (
                        <a
                          href={versionGroupLink}
                          className={cn(
                            versionTitleStyle,
                            "hover:text-remedium-blue underline decoration-solid transition-all [text-decoration-skip-ink:none]",
                          )}
                          target={"_blank"}
                          rel="noreferrer"
                        >
                          {variant.name}
                        </a>
                      )}
                    </div>

                    {/* Vertical list of properties */}
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center gap-2">
                        <span className={labelStyle}>Postać</span>
                        <span className={valueStyle}>
                          {variant.kind || "-"}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={labelStyle}>Dawka</span>
                        <span className={valueStyle}>
                          {variant.dose || "-"}
                        </span>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className={labelStyle}>Opakowanie</span>
                        <span className={valueStyle}>
                          <MedBoxList boxes={boxes} />
                        </span>
                      </div>
                      {priceForRefundFull !== "-" && (
                        <div className="flex items-center gap-2">
                          <span className={labelStyle}>100%</span>
                          <span
                            className={cn(
                              valueStyle,
                              "bg-remedium-blue/10 text-remedium-blue rounded-sm px-2.5",
                            )}
                          >
                            {priceForRefundFull}
                          </span>
                        </div>
                      )}
                      {priceForRefundFifty !== "-" && (
                        <div className="flex items-center gap-2">
                          <span className={labelStyle}>50%</span>
                          <span
                            className={cn(
                              valueStyle,
                              "bg-remedium-blue/10 text-remedium-blue rounded-sm px-2.5",
                            )}
                          >
                            {priceForRefundFifty}
                          </span>
                        </div>
                      )}
                      {priceForRefundThirty !== "-" && (
                        <div className="flex items-center gap-2">
                          <span className={labelStyle}>30%</span>
                          <span
                            className={cn(
                              valueStyle,
                              "bg-remedium-blue/10 text-remedium-blue rounded-sm px-2.5",
                            )}
                          >
                            {priceForRefundThirty}
                          </span>
                        </div>
                      )}
                      {priceForRefundR !== "-" && (
                        <div className="flex items-center gap-2">
                          <span className={labelStyle}>R</span>
                          <span
                            className={cn(
                              valueStyle,
                              "bg-remedium-blue/10 text-remedium-blue rounded-sm px-2.5",
                            )}
                          >
                            {priceForRefundR}
                          </span>
                        </div>
                      )}
                      {getPresentOtherRefunds(variant.prices).length > 0 && (
                        <div className="flex items-center gap-2">
                          <span className={cn(labelStyle, "text-xs")}>
                            Inne refundacje
                          </span>
                          <span className={cn(valueStyle, "flex gap-1")}>
                            {getPresentOtherRefunds(variant.prices).map(
                              (name) => (
                                <Fragment key={name}>
                                  {getPriceForRefundOther(
                                    variant.prices,
                                    name,
                                    versionGroupLink,
                                  )}
                                </Fragment>
                              ),
                            )}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              },
            )}
          </div>
        );
      })}
    </>
  );
}
